import {
  booleanField,
  stringField,
  stringSetField,
} from 'ext-ui/hooks/filters/fieldHelpers'
import {
  buildUseFilterOptions,
  FilterSchema,
} from 'ext-ui/hooks/filters/schemaBuilder'
import { VirtualCardFilterStatus, VirtualCardTypes } from 'src/enums'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { VirtualCardsFiltersBody } from 'src/queries/virtualCards.models'

export type { VirtualCardsFiltersBody } from 'src/queries/virtualCards.models'

export enum VirtualCardsListContext {
  AllCards = 'AllCards',
  MyWallet = 'MyWallet',
  Account = 'Account',
  Accounts = 'Accounts',
  Budget = 'Budget',
}

export interface VirtualCardsFilters {
  search?: string
  recipients?: Set<string>
  statuses?: Set<string>
  cardTypes?: Set<string>
  deactivatingIn?: string
  sortBy?: string
  sortDescending?: boolean
}

export interface VirtualCardsAnalyticsEventProperties {
  card_user?: string[]
  status?: string[]
  search?: string
  card_type?: string[]
  deactivating_in?: string
}

const contextSortDefaults: Record<
  VirtualCardsListContext,
  { sortBy: string; sortDescending: boolean }
> = {
  [VirtualCardsListContext.AllCards]: {
    sortBy: 'activeClosedUpdatedAt',
    sortDescending: false,
  },
  [VirtualCardsListContext.MyWallet]: {
    sortBy: 'activeClosedUpdatedAt',
    sortDescending: false,
  },
  [VirtualCardsListContext.Account]: {
    sortBy: 'activeClosedUpdatedAt',
    sortDescending: false,
  },
  [VirtualCardsListContext.Accounts]: {
    sortBy: 'activeUpdatedAt',
    sortDescending: false,
  },
  [VirtualCardsListContext.Budget]: {
    sortBy: 'activeClosedUpdatedAt',
    sortDescending: false,
  },
}

export const virtualCardStatusGroupToApiStatuses = (
  statuses?: Set<string>,
): VirtualCardStatus[] | undefined => {
  if (!statuses || statuses.size === 0) {
    return undefined
  }

  const apiStatuses = new Set<VirtualCardStatus>()
  if (
    statuses.has(VirtualCardFilterStatus.active) ||
    statuses.has(VirtualCardStatus.Active)
  ) {
    apiStatuses.add(VirtualCardStatus.Active)
  }
  if (
    statuses.has(VirtualCardFilterStatus.pending) ||
    statuses.has(VirtualCardStatus.Pending)
  ) {
    apiStatuses.add(VirtualCardStatus.Pending)
  }
  if (
    statuses.has(VirtualCardFilterStatus.inactive) ||
    statuses.has(VirtualCardStatus.Cancelled) ||
    statuses.has(VirtualCardStatus.Expired) ||
    statuses.has(VirtualCardStatus.Rejected)
  ) {
    apiStatuses.add(VirtualCardStatus.Cancelled)
    apiStatuses.add(VirtualCardStatus.Expired)
    apiStatuses.add(VirtualCardStatus.Rejected)
  }
  if (
    statuses.has(VirtualCardFilterStatus.closed) ||
    statuses.has(VirtualCardStatus.Closed) ||
    statuses.has(VirtualCardStatus.Consumed)
  ) {
    apiStatuses.add(VirtualCardStatus.Closed)
    apiStatuses.add(VirtualCardStatus.Consumed)
  }

  return apiStatuses.size > 0 ? Array.from(apiStatuses) : undefined
}

const apiStatusesToStatusGroups = (statuses?: Set<string>): Set<string> => {
  if (!statuses || statuses.size === 0) {
    return new Set<string>()
  }

  const statusGroups = new Set<string>()
  if (statuses.has(VirtualCardStatus.Active)) {
    statusGroups.add(VirtualCardFilterStatus.active)
  }
  if (statuses.has(VirtualCardStatus.Pending)) {
    statusGroups.add(VirtualCardFilterStatus.pending)
  }
  if (
    statuses.has(VirtualCardStatus.Cancelled) &&
    statuses.has(VirtualCardStatus.Expired) &&
    statuses.has(VirtualCardStatus.Rejected)
  ) {
    statusGroups.add(VirtualCardFilterStatus.inactive)
  }
  if (
    statuses.has(VirtualCardStatus.Closed) &&
    statuses.has(VirtualCardStatus.Consumed)
  ) {
    statusGroups.add(VirtualCardFilterStatus.closed)
  }

  for (const status of statuses) {
    if (
      Object.values(VirtualCardFilterStatus).includes(
        status as VirtualCardFilterStatus,
      )
    ) {
      statusGroups.add(status)
    }
  }

  return statusGroups
}

export function makeBaseVirtualCardsSchema(args: {
  context: VirtualCardsListContext
  userId?: string
  creditCardId?: string
  initialFilters?: Partial<VirtualCardsFilters>
}) {
  const sortDefaults = contextSortDefaults[args.context]
  const init = {
    ...sortDefaults,
    ...args.initialFilters,
  }

  const schema: FilterSchema<VirtualCardsFilters, VirtualCardsFiltersBody> = {
    fields: {
      search: stringField(init.search),
      recipients: stringSetField(init.recipients),
      statuses: stringSetField(init.statuses),
      cardTypes: stringSetField(init.cardTypes),
      deactivatingIn: stringField(init.deactivatingIn),
      sortBy: stringField(init.sortBy),
      sortDescending: booleanField(init.sortDescending),
    },
    finalizeUI: (ui) => ({
      ...ui,
      statuses: apiStatusesToStatusGroups(ui.statuses),
    }),
    finalizeApi: (api, ui) => {
      const out: VirtualCardsFiltersBody = {
        search: api.search?.trim() || undefined,
        recipients: api.recipients,
        statuses: virtualCardStatusGroupToApiStatuses(ui.statuses),
        cardTypes: api.cardTypes as VirtualCardTypes[] | undefined,
        deactivatingIn: api.deactivatingIn,
        sortField: ui.sortBy || sortDefaults.sortBy,
        sortDirection:
          (ui.sortDescending ?? sortDefaults.sortDescending) === false
            ? 'ASC'
            : 'DESC',
      }

      switch (args.context) {
        case VirtualCardsListContext.MyWallet:
          out.recipient = args.userId
          delete out.recipients
          break
        case VirtualCardsListContext.Account:
        case VirtualCardsListContext.Budget:
          out.creditCardId = args.creditCardId
          out.issued = true
          break
        case VirtualCardsListContext.Accounts:
          out.cardholderOrViewer = 'me'
          out.issued = true
          break
        case VirtualCardsListContext.AllCards:
        default:
          break
      }

      return Object.fromEntries(
        Object.entries(out).filter(([, value]) => {
          if (value === undefined || value === '') return false
          if (Array.isArray(value) && value.length === 0) return false
          return true
        }),
      ) as VirtualCardsFiltersBody
    },
    finalizeQuery: (flat, ui) => {
      const query = { ...flat }
      if ((ui.sortBy ?? sortDefaults.sortBy) === sortDefaults.sortBy) {
        delete query.sortBy
      }
      if (
        (ui.sortDescending ?? sortDefaults.sortDescending) ===
        sortDefaults.sortDescending
      ) {
        delete query.sortDescending
      }
      return query
    },
  }

  return {
    ...buildUseFilterOptions(schema),
    excludedFilterKeysFromCount: ['sortBy', 'sortDescending'] as Array<
      keyof VirtualCardsFilters
    >,
  }
}
